import { useState } from 'react';
import { FieldTimeOutlined, FileTextOutlined, PictureOutlined, PlaySquareOutlined } from '@ant-design/icons';

import { DataTable } from '../../../../components/shared';
import { Button, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useBaoCaoInstance, useBaoCaoLuot } from '../../hooks/useLichSu';
import { gomTepTheoLoi, laMaLuotNative, type DongBaoCao } from '../../nghiep-vu/lich-su';
import type { BaoCaoLoi, SuKienLoi, TepBangChung, ThongTinLoiSuKien } from '../../types';
import { HopDongThoiGian } from './HopDongThoiGian';
import { HopXemTep } from './HopXemTep';
import { BadgeMucDo } from './nhan';

type DaKhop = Extract<ThongTinLoiSuKien, { matched: true }>;

const COT_TEP = [
  { kind: 'video', tieuDe: 'rpaBaoCao.cotVideo', nut: 'rpaBaoCao.xemVideo', icon: <PlaySquareOutlined /> },
  { kind: 'screenshot', tieuDe: 'rpaBaoCao.cotAnh', nut: 'rpaBaoCao.xemAnh', icon: <PictureOutlined /> },
  { kind: 'log', tieuDe: 'rpaBaoCao.cotLog', nut: 'rpaBaoCao.xemLog', icon: <FileTextOutlined /> },
] as const;

/**
 * Báo cáo lỗi Robot gửi về (`report-drive.json` của native), dùng ở hộp lỗi của Lịch sử và hộp chi tiết Sự cố. Gắn
 * theo InstanceId của OpenRPA; lượt không có InstanceId (lượt Native gửi lệnh rồi hỏng) thì theo mã `RUN-…`.
 *
 * Loại lỗi, nguyên nhân và cách xử lý tra theo Cấu hình lỗi HIỆN TẠI lúc đọc: đổi setting là đổi cách đọc báo cáo cũ.
 */
export function KhoiBaoCaoLoi({ instanceId = '', runCode = '' }: { instanceId?: string; runCode?: string }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const theoInstance = useBaoCaoInstance(instanceId);
  const theoLuot = useBaoCaoLuot(instanceId ? '' : runCode);
  const q = instanceId ? theoInstance : theoLuot;

  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">{t('rpaBaoCao.tieuDe')}</h3>
      {!instanceId && !runCode ? (
        <p className="text-xs text-content-muted">{t('rpaBaoCao.chuaCo')}</p>
      ) : q.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : q.isError ? (
        <p role="alert" className="text-xs text-warning-700">
          {dichLoi(q.error)}
        </p>
      ) : q.data ? (
        <ThanBaoCao bc={q.data} />
      ) : (
        <p className="text-xs text-content-muted">{t('rpaBaoCao.chuaCo')}</p>
      )}
    </section>
  );
}

function ThanBaoCao({ bc }: { bc: BaoCaoLoi }) {
  const { t, locale } = useT();
  const [dangXem, setDangXem] = useState<TepBangChung | null>(null);
  const [xemDongThoiGian, setXemDongThoiGian] = useState(false);
  const dong = gomTepTheoLoi(bc);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 break-words text-xs text-content-muted">
          {t('rpaBaoCao.capNhat', {
            luc: ngayGioVN(bc.updatedAt, locale),
            lan: soVN(bc.revision, locale),
            ma: bc.executionId,
          })}
        </p>
        {laMaLuotNative(bc.runId) && (
          <Button variant="ghost" size="sm" icon={<FieldTimeOutlined />} onClick={() => setXemDongThoiGian(true)}>
            {t('rpaBaoCao.xemDongThoiGian')}
          </Button>
        )}
      </div>
      <DataTable<DongBaoCao>
        caption={t('rpaBaoCao.tieuDe')}
        dataSource={dong}
        rowKey="key"
        size="small"
        bordered
        pagination={false}
        locale={{ emptyText: <span className="text-xs text-content-muted">{t('rpaBaoCao.khongCoLoi')}</span> }}
        columns={[
          {
            title: t('rpaBaoCao.cotLoi'),
            key: 'loi',
            width: 320,
            render: (_, d) => (
              <div className="min-w-0 whitespace-normal">
                <span className="break-words font-medium text-content-primary">
                  {d.chung ? t('rpaBaoCao.tepChung') : d.event?.step?.trim() || t('rpaBaoCao.chuaCoTenBuoc')}
                </span>
                {d.event && <TomTatLoi suKien={d.event} />}
              </div>
            ),
          },
          ...COT_TEP.map((c) => ({
            title: t(c.tieuDe),
            key: c.kind,
            render: (_: unknown, d: DongBaoCao) => {
              const tep = d.tep.filter((x) => x.kind === c.kind);
              return tep.length ? (
                <div className="flex flex-col items-start gap-1.5">
                  {tep.map((x) => (
                    <Button key={x.id} variant="outline" size="sm" icon={c.icon} title={x.fileName} onClick={() => setDangXem(x)}>
                      {t(c.nut)}
                    </Button>
                  ))}
                </div>
              ) : (
                <span className="whitespace-nowrap text-xs text-content-muted">{t('rpaBaoCao.chuaCoTep')}</span>
              );
            },
          })),
        ]}
        expandable={{
          rowExpandable: (d) => Boolean(d.event),
          expandedRowRender: (d) => (d.event ? <ChiTietSuKien suKien={d.event} /> : null),
          columnTitle: <span className="sr-only">{t('rpaBaoCao.chiTietLoi')}</span>,
          columnWidth: 36,
        }}
      />
      {!bc.attachments.some((a) => a.kind === 'video') && (
        <p className="text-xs text-warning-700">{t('rpaBaoCao.chuaCoVideo')}</p>
      )}
      <p className="text-xs text-content-muted">{t('rpaBaoCao.ghiChuLuuTru')}</p>
      {dangXem && <HopXemTep executionId={bc.executionId} tep={dangXem} onClose={() => setDangXem(null)} />}
      {xemDongThoiGian && bc.runId && <HopDongThoiGian runCode={bc.runId} onClose={() => setXemDongThoiGian(false)} />}
    </>
  );
}

/** Mã + tên loại lỗi, mức độ và mô tả theo setting lỗi hiện tại. */
function TomTatLoi({ suKien }: { suKien: SuKienLoi }) {
  const { t } = useT();
  const info = suKien.errorInfo;
  if (!info) {
    return suKien.errorCode ? (
      <p className="mt-0.5 text-xs text-content-muted">{t('rpaBaoCao.maLoi', { ma: suKien.errorCode })}</p>
    ) : null;
  }
  if (!info.matched) {
    return <p className="mt-0.5 text-xs text-content-muted">{t('rpaBaoCao.chuaThuocLoai', { ten: info.name })}</p>;
  }
  return (
    <div className="mt-1 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <BadgeMucDo mucDo={info.severity} />
        <span className="text-content-muted">
          {info.code} · {info.name}
        </span>
      </div>
      {info.description && <p className="text-xs text-content-secondary">{info.description}</p>}
    </div>
  );
}

function CachXuLy({ info }: { info: DaKhop }) {
  const { t, locale } = useT();
  return (
    <>
      {info.maxRetries > 0
        ? t('rpaBaoCao.thuLai', { lan: soVN(info.maxRetries, locale), giay: soVN(info.retryDelaySeconds, locale) })
        : t('rpaBaoCao.khongThuLai')}
      <span className={info.onExhausted === 'STOP' ? 'block text-xs text-danger-700' : 'block text-xs text-content-muted'}>
        {t(info.onExhausted === 'STOP' ? 'rpaBaoCao.hetLanDung' : 'rpaBaoCao.hetLanBoQua')}
      </span>
    </>
  );
}

function ChiTietSuKien({ suKien }: { suKien: SuKienLoi }) {
  const { t, locale } = useT();
  const info = suKien.errorInfo;
  return (
    <div className="min-w-0 space-y-2 text-left">
      <p className="text-sm font-medium text-content-primary">{suKien.step || KHONG_CO}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-muted">
        <span>{ngayGioVN(suKien.capturedAt || suKien.timestamp, locale)}</span>
        {suKien.errorCode && <span>{t('rpaBaoCao.maLoi', { ma: suKien.errorCode })}</span>}
        {suKien.retry && <span>{t('rpaBaoCao.lanThu', { lan: suKien.retry })}</span>}
      </div>
      {info?.matched ? (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-lg bg-subtle px-3 py-2 text-sm sm:grid-cols-[8rem_1fr]">
          {info.description && (
            <>
              <dt className="text-content-muted">{t('rpaBaoCao.nguyenNhan')}</dt>
              <dd className="text-content-primary">{info.description}</dd>
            </>
          )}
          <dt className="text-content-muted">{t('rpaBaoCao.heThongXuLy')}</dt>
          <dd className="text-content-primary">
            <CachXuLy info={info} />
          </dd>
          {info.classification && (
            <>
              <dt className="text-content-muted">{t('rpaBaoCao.nhomLoi')}</dt>
              <dd className="text-content-primary">{info.classification}</dd>
            </>
          )}
        </dl>
      ) : suKien.errorCode || info ? (
        <p className="text-xs text-warning-700">{t('rpaBaoCao.chuaPhanLoai')}</p>
      ) : null}
      <div>
        <p className="mb-0.5 text-xs text-content-muted">{t('rpaBaoCao.thongBaoGoc')}</p>
        <p className="whitespace-pre-wrap break-words text-sm text-danger-700">{suKien.message || t('rpaBaoCao.khongCoThongBao')}</p>
      </div>
      {suKien.exception && (
        <details>
          <summary className="cursor-pointer text-xs text-content-muted">{t('rpaBaoCao.chiTietException')}</summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-subtle px-3 py-2 text-xs">
            {suKien.exception}
          </pre>
        </details>
      )}
    </div>
  );
}
